"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Button, useToast } from "@wmart/ui";
import { useCart } from "@/app/[locale]/(shop)/cart/CartProvider";
import { nearestValidQty } from "../src/lib/quantity-helpers";

/**
 * Qty stepper + Add-to-cart for the PDP and any single-variant context.
 *
 * Wires into the existing `<CartProvider>` (path-stable from V1.0). On a
 * successful add, surfaces a success toast; on failure, surfaces a danger
 * toast carrying the provider's mapped error message. The provider already
 * formats 401 / 403 / 410 / 422 / etc. into user-friendly copy, so we don't
 * try to second-guess the error text here.
 *
 * Disabled handling:
 *  - `disabled` prop (e.g. OOS variant) blocks the Add button AND the stepper
 *    (−/+/input) so an out-of-stock SKU offers no add affordance at all —
 *    a bare enabled stepper next to a dead Add button reads as "addable".
 *
 * The component owns ONLY `qty` + `busy` state. Cart state lives in the
 * provider so the sticky header badge updates immediately after a push.
 *
 * MOQ / increment (P14a-T13):
 *  - `minOrderQuantity` (default 1) — lowest allowed qty; add-to-cart is
 *    blocked below this value.
 *  - `orderIncrement` (default 1) — qty must be a multiple of this value;
 *    on stepper blur the entered value is rounded up to the nearest valid qty.
 *  - `onQuantityChange` — fires on every qty change so the parent (PDP) can
 *    pass the current qty into `<VolumeBreakLadder currentQuantity>`.
 *  - Initial qty defaults to MOQ so the add button is never blocked on mount.
 */
interface QtyAndAddProps {
  variantId: string;
  initialQuantity?: number;
  disabled?: boolean;
  productTitle: string;
  minOrderQuantity?: number;
  orderIncrement?: number;
  onQuantityChange?: (qty: number) => void;
}

export function QtyAndAdd({
  variantId,
  initialQuantity,
  disabled,
  productTitle,
  minOrderQuantity = 1,
  orderIncrement = 1,
  onQuantityChange,
}: QtyAndAddProps) {
  const t = useTranslations("Product");
  const tCommon = useTranslations("Common");
  const moq = Math.max(1, minOrderQuantity);
  const inc = Math.max(1, orderIncrement);

  // Start at MOQ so the add button is never blocked on mount.
  const startQty = initialQuantity != null ? Math.max(moq, initialQuantity) : moq;
  const [qty, setQty] = useState(startQty);
  const [busy, setBusy] = useState(false);
  const { addItem } = useCart();
  const toast = useToast();

  const applyQty = (next: number) => {
    setQty(next);
    onQuantityChange?.(next);
  };

  const decrement = () => {
    const next = Math.max(moq, qty - inc);
    applyQty(next);
  };

  const increment = () => {
    applyQty(qty + inc);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value, 10);
    if (!Number.isFinite(parsed) || parsed < moq) {
      applyQty(moq);
      return;
    }
    // Round up to the nearest valid qty (ceil of nearestValidQty tuple).
    const [, ceil] = nearestValidQty(parsed, moq, inc);
    applyQty(ceil);
  };

  const isInvalid = qty < moq || qty % inc !== 0;

  const onAdd = async () => {
    setBusy(true);
    try {
      await addItem({ variantId, quantity: qty });
      toast.push({
        tone: "success",
        title: t("addedToCart"),
        body: t("addedToCartBody", { qty, title: productTitle }),
      });
    } catch (err) {
      toast.push({
        tone: "danger",
        title: t("couldNotAdd"),
        body: err instanceof Error ? err.message : tCommon("tryAgain"),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <div
          data-slot="qty-stepper"
          className="inline-flex h-10 items-center rounded-sm border border-ink-200 bg-white disabled:opacity-60 [&:has(:disabled)]:opacity-60"
        >
          <Button
            variant="ghost"
            size="sm"
            icon="minus"
            aria-label={t("decreaseQuantity")}
            onClick={decrement}
            disabled={disabled}
          />
          <input
            type="number"
            min={moq}
            step={inc}
            value={qty}
            disabled={disabled}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              if (Number.isFinite(parsed) && parsed > 0) {
                applyQty(parsed);
              }
            }}
            onBlur={onBlur}
            className="numeric w-12 select-none bg-transparent text-center text-body text-ink-900 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none disabled:cursor-not-allowed"
            aria-label={t("quantity")}
          />
          <Button
            variant="ghost"
            size="sm"
            icon="plus"
            aria-label={t("increaseQuantity")}
            onClick={increment}
            disabled={disabled}
          />
        </div>
        <Button
          onClick={onAdd}
          icon="cart"
          loading={busy}
          disabled={disabled || isInvalid}
          className="min-w-0 flex-1"
        >
          {tCommon("addToCart")}
        </Button>
      </div>
      {(moq > 1 || inc > 1) && (
        <p className="text-ink-500 text-xs">
          {moq > 1 && t("minOrderQty", { qty: moq })}
          {moq > 1 && inc > 1 && " · "}
          {inc > 1 && t("orderIncrement", { step: inc })}
        </p>
      )}
    </div>
  );
}
